import classNames                                         from 'classnames';
import React, { CSSProperties, FC, ReactNode, useEffect } from 'react';
import { Icon, IconProps, Popup, SemanticICONS }          from 'semantic-ui-react';

import { Align, AsTagProp, Position, SemanticUIColor } from '../../../types';
import { CONTEXT_COLORS, CONTEXTS }                    from '../../../constants/ContextEnums';
import { StyledContainer }                             from '../../containers';
import { ClassValue }                                  from "classnames/types";


interface ItemWithNotifierProps extends AsTagProp {

    /* Content or label to produce */
    content: ReactNode,

    /* What context the notification is in, eg: error / success */
    notifyContext?: null | CONTEXTS,

    /* Display style for the notifier */
    notifyStyle?: 'icon' | 'dot' | 'custom',

    notifyIcon?: Partial<Pick<IconProps, 'name' | 'color' | 'style'>>,

    /* Offset from the top of the element. Accepts negative numbers */
    notificationYOffset?: number,

    /* Offset from the right side of the element. Accepts negative numbers */
    notificationXOffset?: number,

    /* Size of the notification element; numeric for 'dot', semantic size for 'icon'. */
    notificationSize?: number | IconProps['size'],

    /* Content that will appear on notifier rollover if present */
    notificationHelp?: ReactNode,

    /* Horizontal starting edge position of the notifier on the element */
    notificationPosition?: Align.RIGHT | Align.LEFT,

    /* Additional styles to apply to notification container */
    notificationStyle?: CSSProperties,

    /* Should this notifier bring attention to itself by pulsing */
    pulseNotifier?: boolean

    /* Dependencies to watch */
    dependencies?: any[],

    notificationContent?: ReactNode,

    notificationWrapperClassnames?: ClassValue,

    [ additionalProps: string ]: any
}

const defaultProps: ItemWithNotifierProps = {
    as:                   'span',
    content:              'Item',
    notifyStyle:          'dot',
    notifyIcon:           null,
    notifyContext:        null,
    notificationYOffset:  -5,
    notificationXOffset:  -10,
    notificationSize:     null,
    notificationHelp:     null,
    notificationPosition: Align.RIGHT,
    notificationStyle:    {},
    notificationContent:  null,
    pulseNotifier:        false,
    dependencies:         [],
}

const ItemWithNotifier: FC<ItemWithNotifierProps> = ( props ) => {

    let { notifyContext, notifyStyle, notificationHelp } = props,
        { notificationYOffset, notificationXOffset }     = props,
        { notificationSize, pulseNotifier }              = props;

    if( !notificationSize ){
        notificationSize = ( notifyStyle === 'dot' ) ? 8 : 'small';
    }

    useEffect( () => {

        // Update state when a dependency changes

    }, props.dependencies )

    /* Setup the notifcation style defintions */
    let notificationTypes = {
        [ CONTEXTS.INFO ]:    { color: CONTEXT_COLORS[ CONTEXTS.INFO ], icon: 'info circle', },
        [ CONTEXTS.WARNING ]: { color: CONTEXT_COLORS[ CONTEXTS.WARNING ], icon: 'warning sign', },
        [ CONTEXTS.ERROR ]:   { color: CONTEXT_COLORS[ CONTEXTS.ERROR ], icon: 'close', },
        [ CONTEXTS.SUCCESS ]: { color: CONTEXT_COLORS[ CONTEXTS.SUCCESS ], icon: 'check square', },
    }

    let notificationColor    = notifyContext ? notificationTypes[ notifyContext ].color as SemanticUIColor : null,
        notificationIcon     = notifyContext ? notificationTypes[ notifyContext ].icon as SemanticICONS : null,
        notificationPosition = ( props.notificationPosition === Align.LEFT )
                               ? ( { left: notificationXOffset } )
                               : ( { right: notificationXOffset } ),
        notificationStyle    = { style: props.notificationStyle || {} };

    /* Setup the item components */
    let notificationItems = {
        dot: () => (
            <StyledContainer position={ Position.ABSOLUTE }
                             top={ notificationYOffset }
                             right={ notificationXOffset }
                             style={ { width: notificationSize, height: notificationSize, ...notificationStyle } }
                             bgColor={ notificationColor }
                             className={ classNames( 'mask--circle', { [ `pulse-${ notificationColor }` ]: pulseNotifier } ) }
            />
        ),

        custom: () => (
            <StyledContainer position={ Position.ABSOLUTE }
                             top={ notificationYOffset }
                             right={ notificationXOffset }
                             style={ notificationStyle }
                             bgColor={ notificationColor }>
                { props.notificationContent }
            </StyledContainer>
        ),

        icon: () => (
            <StyledContainer position={ Position.ABSOLUTE }
                             top={ notificationYOffset }
                             { ...notificationPosition }
                             { ...notificationStyle }
                             className={ classNames( { [ `pulse-${ notificationColor }` ]: pulseNotifier } ) }>
                <Icon color={ notificationColor } name={ notificationIcon } size={ notificationSize as IconProps['size'] } { ...( props.notifyIcon ?? {} ) } fitted/>
            </StyledContainer>
        ),
    };

    /* Craft which type to use */
    let notificationItem = ( notifyContext || notifyStyle === 'custom' ) ? notificationItems[ notifyStyle ]() : null;

    /* Create a popover if we have indicated rollover content */
    if( notificationItem && notificationHelp ){
        notificationItem = <Popup position={ 'top center' }
                                  size={ 'small' }
                                  trigger={ notificationItem }
                                  content={ notificationHelp }
                                  popperModifiers={ {
                                      preventOverflow: {
                                          mainAxis:          false,
                                          boundariesElement: "window"
                                      }
                                  } }
                                  popperDependencies={ props.dependencies }/>
    }

    return (
        <StyledContainer as={ props.as } position={ Position.RELATIVE } className={ classNames( props.notificationWrapperClassnames ) }>
            { props.content }
            { notificationItem }
        </StyledContainer>
    );
};

ItemWithNotifier.defaultProps = defaultProps;

/* For use with a tabbed form component in
 order to show errors across tabs */
const FormTabWithNotifier: FC<{

    /* Tab's name, displayed in a Menu.Item for Tab Component */
    label: string,

    /* Does this tab contain errors? */
    active: boolean,

    /* Content to describe error, defaults to generic messaging. */
    notificationHelp?: ItemWithNotifierProps['notificationHelp']

}> = ( props ) => (

    <ItemWithNotifier
        notifyStyle={ 'dot' }
        content={ props.label }
        notifyContext={ props.active ? CONTEXTS.ERROR : null }
        notificationHelp={ props.notificationHelp || 'Field values on this tab may be invalid or need attention.' }/>
);

export { ItemWithNotifier, FormTabWithNotifier }
